import { Prisma, type FinancialAccount } from '@prisma/client';
import { audit, type AuditActor } from '../../core/audit.js';
import { encrypt } from '../../core/crypto.js';
import { HttpError } from '../../core/errors.js';
import { toDate } from '../accounting/periods.js';
import { postEntry } from '../accounting/posting.service.js';

type Tx = Prisma.TransactionClient;

const D = (v: Prisma.Decimal.Value) => new Prisma.Decimal(v);

/** Ledger code ranges per kind, so bank, mobile money and cash accounts group together. */
export const CODE_RANGE: Record<string, [number, number]> = {
  cash: [1010, 1049],
  bank: [1100, 1149],
  mobile_money: [1150, 1189],
  gateway: [1190, 1199],
};

export const FINANCIAL_ACCOUNT_KINDS = ['bank', 'mobile_money', 'cash', 'gateway'] as const;

export interface CreateFinancialAccountInput {
  kind: (typeof FINANCIAL_ACCOUNT_KINDS)[number];
  name: string;
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  branch?: string;
  currency: string;
  openingBalance: string;
  openingDate: string;
}

/**
 * Creates a financial account: allocates the next free ledger code for its kind, opens the
 * FinancialAccount row, and posts an opening-balance entry (Dr/Cr OPENING_EQ) if non-zero.
 * Shared by the banking route and internal scripts (e.g. the demo-data seed), so there is one
 * place that owns ledger-code allocation and the opening posting.
 */
export async function createFinancialAccount(
  tx: Tx,
  encryptionKey: string,
  actor: AuditActor & { userId: number },
  body: CreateFinancialAccountInput,
): Promise<FinancialAccount> {
  const companyId = actor.companyId;
  // Serialise ledger-code allocation: two accounts created at once must not pick the same code.
  await tx.$queryRaw`SELECT id FROM companies WHERE id = ${companyId} FOR UPDATE`;
  const [lo, hi] = CODE_RANGE[body.kind]!;
  const used = await tx.account.findMany({
    where: { companyId, code: { gte: String(lo), lte: String(hi) } },
    select: { code: true },
  });
  const taken = new Set(used.map((u) => Number(u.code)));
  let code = lo;
  while (taken.has(code) && code <= hi) code += 1;
  if (code > hi)
    throw new HttpError(409, 'NO_CODES', 'No free ledger codes left for this account type');

  const ledger = await tx.account.create({
    data: {
      companyId,
      code: String(code),
      name: body.name,
      type: 'asset',
      subtype: body.kind,
      isCash: true,
      isSystem: false,
    },
  });
  const fa = await tx.financialAccount.create({
    data: {
      companyId,
      kind: body.kind,
      name: body.name,
      bankName: body.bankName,
      accountName: body.accountName,
      accountNumberEnc: body.accountNumber ? encrypt(body.accountNumber, encryptionKey) : undefined,
      accountNumberLast4: body.accountNumber ? body.accountNumber.slice(-4) : undefined,
      branch: body.branch,
      currency: body.currency,
      openingBalance: body.openingBalance,
      openingDate: toDate(body.openingDate),
      ledgerAccountId: ledger.id,
      createdBy: actor.userId,
    },
  });
  const opening = D(body.openingBalance);
  if (!opening.isZero()) {
    await postEntry(tx, {
      companyId,
      date: body.openingDate,
      description: `Opening balance: ${body.name}`,
      sourceType: 'opening',
      sourceId: fa.id,
      userId: actor.userId,
      lines: opening.isPositive()
        ? [
            { accountId: ledger.id, debit: opening.toFixed(4) },
            { systemKey: 'OPENING_EQ', credit: opening.toFixed(4) },
          ]
        : [
            { systemKey: 'OPENING_EQ', debit: opening.abs().toFixed(4) },
            { accountId: ledger.id, credit: opening.abs().toFixed(4) },
          ],
    });
  }
  await audit(tx, actor, {
    action: 'banking.account_created',
    entity: 'financial_account',
    entityId: fa.id,
    after: { name: fa.name, kind: fa.kind, currency: fa.currency, ledgerCode: ledger.code },
  });
  return fa;
}
